import { DateTime } from "luxon";

import { feedItemsLog } from "@storyboard/event-log/specs/feedItems";
import type { BootstrapEventLogQuery, BootstrapWorkspaceFeedItemEventLog, WsItem } from "@storyboard/web-client";
import { listFeedItemEventLog, serverNow } from "@storyboard/web-client";
import { transport } from "@/config";
import { enqueueCalculateUnreads, flushCalculateUnreads } from "@/models/actions/calculateUnreads";
import { type WarmupSettings, warmupSettingsFor } from "@/models/actions/warmupConstants";
import { type ContiguousSpan, mergeContiguousRange } from "@/models/feedContiguity";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { registryCoverageStore } from "@/models/sync/cursorStore";
import { bindEventLog, coverageKeyFor, type EventLogScope } from "@/models/sync/eventLogClient";
import { newestCreatedAtOf } from "@/models/sync/eventLogPage";
import {
  COVERAGE_HEAD_LAG_MS,
  type CoverageStore,
  type CoveredSpan,
  type LogCoverage,
  mergeSpan,
  normalizeCoverage,
} from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { recordEvent } from "@/services/telemetry";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Feed } from "@/store/types";
import { mapWithConcurrency } from "@/utils/mapWithConcurrency";

/** Pages one gap fill may take before it stops; its progress is kept, and the next sync resumes from it. */
export const FEED_ITEM_MAX_DELTA_PAGES = 50;

/** What one walk of the item log reads: the whole workspace, or a batch of feeds through the `feedIds` lens. */
export type FeedItemLogScope = EventLogScope<typeof feedItemsLog.lenses, typeof feedItemsLog.shape>;

/** The scope the workspace-wide warmup walks: no lens, no shape. */
const WORKSPACE_SCOPE: FeedItemLogScope = { lenses: {}, shape: {} };

/** Registry key of the workspace-wide walk's coverage. */
export const FEED_ITEM_COVERAGE_KEY = coverageKeyFor(feedItemsLog, WORKSPACE_SCOPE);

/** How many batches of feeds walk at once under the `feeds` scope. */
export const FEED_BATCH_CONCURRENCY = 4;

/**
 * The scope of one batch of feeds, sorted so the same feeds always share one coverage key.
 *
 * A batch is keyed by its ids, so coverage carries over between boots while the batches fall the same way. A
 * candidate set that changes shifts the batches, and a batch under a new key walks its lookback again; items upsert
 * by id, so that costs requests, not correctness.
 */
export function feedBatchScope(feedIds: readonly string[]): FeedItemLogScope {
  return { lenses: { feedIds: [...feedIds].sort() }, shape: {} };
}

/** What every batch key starts with, so the keys of batches a boot no longer walks can be found and dropped. */
const FEED_BATCH_KEY_PREFIX = coverageKeyFor(feedItemsLog, feedBatchScope(["*"])).split("*")[0] ?? "";

/**
 * The steps the `workspace` mode's walk loads in, in hours before now: the last twelve hours first, then the rest of the
 * lookback in the background.
 */
export const FEED_ITEM_WORKSPACE_LADDER_HOURS: readonly number[] = [12];

/**
 * A batch of feeds loads its whole lookback in one step: a batch's read is cheap, so there is no slow read to land
 * the newest rows ahead of.
 */
export const FEED_BATCH_LADDER_HOURS: readonly number[] = [];

/** One page as the engine sees it: the route's page, with the request, its scope and its arrival. */
type FeedItemLogPage = BootstrapWorkspaceFeedItemEventLog & {
  ctx: SyncContext;
  scope: FeedItemLogScope;
  since: Date;
  /** The read's upper edge when the gap had one; the last page then holds up to it, not up to the head. */
  until?: Date;
  arrived: Date;
};

/**
 * One page as it lands: its items, and the span of the log whose every row is now held, which contains the page.
 *
 * `covered` is null when the page proves nothing: an empty page that still carries a cursor. `feedIds` names the
 * feeds the page's read was scoped to; absent for a workspace-wide read, whose span holds every readable feed.
 */
export type LandedFeedItemPage = {
  items: WsItem[];
  covered: CoveredSpan | null;
  /** Whether `covered` reaches the head this session's walk of the same read carried coverage to. */
  reachesHead: boolean;
  feedIds?: readonly string[];
};

/**
 * The span of the log a delta page proves held, on the coverage engine's terms.
 *
 * The last page holds everything up to the read's `until`, or, for a read open to the head, up to its arrival less the
 * lag. A middle page holds up to its newest row less a millisecond, because a page boundary can split rows that share a
 * `createdAt`.
 */
export function heldSpanOf(page: {
  items: readonly WsItem[];
  cursor?: string | null;
  since: Date;
  until?: Date;
  arrived: Date;
}): CoveredSpan | undefined {
  if (!page.cursor) {
    const to = page.until ?? DateTime.fromJSDate(page.arrived).minus({ milliseconds: COVERAGE_HEAD_LAG_MS }).toJSDate();
    return { from: page.since.toISOString(), to: to.toISOString() };
  }
  const newest = newestCreatedAtOf(page.items);
  if (newest === undefined) return undefined;
  return {
    from: page.since.toISOString(),
    to: DateTime.fromJSDate(newest).minus({ milliseconds: 1 }).toJSDate().toISOString(),
  };
}

const time = (iso: string) => new Date(iso).getTime();

/**
 * Folds one page's items of a feed into the feed's contiguity span.
 *
 * Every row of the log inside `covered` is held, so the feed's items in it are contiguous with each other, and with
 * the feed's span when the span reaches into `covered`. A span that does not is left as it stands: the rows between
 * the two were never fetched. A feed with no span takes the page's items as its span.
 *
 * @param current - The feed's persisted span; unknown unless both bounds are set.
 * @param createdAts - `createdAt` of the feed's items on the page.
 * @param covered - The held span of the log that contains the page; null when the page proves nothing.
 * @param reachesHead - Whether `covered` reaches the head of the log: the page was the walk's last.
 * @returns The span, and whether every item of the feed up to the head is now held.
 */
export function foldFeedSpan(
  current: { oldest?: string | null; newest?: string | null } | null | undefined,
  createdAts: readonly string[],
  covered: CoveredSpan | null,
  reachesHead: boolean,
): { span: ContiguousSpan | null; held: boolean } {
  const span = current?.oldest && current?.newest ? { oldest: current.oldest, newest: current.newest } : null;
  if (covered === null || createdAts.length === 0) return { span, held: false };
  if (span !== null && (time(span.newest) < time(covered.from) || time(span.oldest) > time(covered.to))) {
    return { span, held: false };
  }
  return { span: mergeContiguousRange(span, createdAts, "anchored"), held: reachesHead };
}

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type FeedItemSyncDeps = {
  /** How far back the walk holds the workspace's items; the workspace's `lookbackHours` in production. */
  depth: { hours: number };
  fetchPage: (query: BootstrapEventLogQuery) => Promise<BootstrapWorkspaceFeedItemEventLog>;
  coverage: CoverageStore;
  land: (ctx: SyncContext, page: LandedFeedItemPage) => Promise<void>;
  /** A recoverable condition worth a reliability event: a walk that hit its cap, a background load that failed. */
  report: (name: string, attributes: Record<string, unknown>) => void;
  /** The server's clock as the client estimates it from every response's `Date` header. */
  now: () => Date;
};

export type FeedItemSync = {
  /** Brings the workspace's last `depth` of items up to date in one workspace-wide walk; see `CoverageSync.sync`. */
  sync: (ctx: SyncContext) => Promise<void>;
  /**
   * Brings the last `depth` of `feedIds`' items up to date, `feedsPerBatch` feeds per read, a few batches at a time.
   *
   * Each batch loads its whole lookback in one step, `FEED_BATCH_CONCURRENCY` batches at a time, and the call resolves
   * once every batch has landed. The stored coverage of batches this call does not walk is dropped first, so a pick
   * that shifts the batches does not leave the old keys behind.
   */
  syncFeeds: (ctx: SyncContext, feedIds: readonly string[], feedsPerBatch: number) => Promise<void>;
  /**
   * Resolves once no background fill is running for the workspace's walk, nor for any batch the last `syncFeeds` of
   * the workspace walked; at once when none is. Never rejects. A boot awaits this after its walk to hold the lookback.
   */
  backfilled: (ctx: SyncContext) => Promise<void>;
};

/**
 * Binds the coverage engine to the feed-item event log, through `bindEventLog` on `feedItemsLog`.
 *
 * The workspace's readable items are one log on the `createdAt` axis, read in delta mode: `since` and the server's
 * cursor, never `feedId` and never history mode. The engine decides what to fetch. This binding lands each page and
 * tells the store which span of the log the page proves held, so each feed's contiguity span can grow with it.
 *
 * The engine records a page's progress only after the page lands, so the binding keeps its own copy of the held spans:
 * read from the store once per workspace, then grown by every page in the same way the engine grows its own.
 */
export function createFeedItemSync(deps: FeedItemSyncDeps): FeedItemSync {
  /** Coverage by `${workspaceId}:${coverage key}`: one entry per workspace-wide walk or batch. */
  const held = new Map<string, LogCoverage>();

  async function heldSpans(workspaceId: string, key: string): Promise<LogCoverage> {
    const at = `${workspaceId}:${key}`;
    const known = held.get(at);
    if (known !== undefined) return known;
    const now = deps.now();
    const floor = DateTime.fromJSDate(now).minus({ hours: deps.depth.hours }).toJSDate();
    const stored = normalizeCoverage(await deps.coverage.get(key, workspaceId), floor, now);
    // A page that landed while the store was read already holds the newer copy.
    const raced = held.get(at);
    if (raced !== undefined) return raced;
    held.set(at, stored);
    return stored;
  }

  /**
   * The head edge each walk reached this session, by `${workspaceId}:${coverage key}`: the `to` of the last page of a
   * read open to the head. A bounded step below it reaches the head through the span it joins.
   */
  const heads = new Map<string, number>();

  async function landPage(page: FeedItemLogPage): Promise<void> {
    const proven = heldSpanOf(page);
    const key = coverageKeyFor(feedItemsLog, page.scope);
    const at = `${page.ctx.workspaceId}:${key}`;
    let covered: CoveredSpan | null = null;
    if (proven !== undefined) {
      const spans = mergeSpan(await heldSpans(page.ctx.workspaceId, key), proven);
      held.set(at, spans);
      covered =
        spans.spans.find((span) => time(span.from) <= time(proven.from) && time(span.to) >= time(proven.to)) ?? null;
      if (!page.cursor && page.until === undefined) heads.set(at, time(proven.to));
    }
    const head = heads.get(at);
    const feedIds = page.scope.lenses.feedIds;
    await deps.land(page.ctx, {
      items: page.items,
      covered,
      reachesHead: covered !== null && head !== undefined && time(covered.to) >= head,
      ...(feedIds === undefined ? {} : { feedIds }),
    });
  }

  /** One binding per ladder: the workspace walk and the batches share the landing and the held spans. */
  const bindWithLadder = (ladderHours: readonly number[]) =>
    bindEventLog<typeof feedItemsLog.lenses, typeof feedItemsLog.shape, FeedItemLogPage>(feedItemsLog, {
      depth: deps.depth,
      ladderHours,
      maxPages: FEED_ITEM_MAX_DELTA_PAGES,
      coverage: deps.coverage,
      fetchPage: async (ctx, scope, query) => {
        const feedIds = scope.lenses.feedIds;
        const page = await deps.fetchPage({
          workspaceId: ctx.workspaceId,
          ...query,
          ...(feedIds === undefined ? {} : { feedIds: feedIds.join(",") }),
        });
        return {
          ...page,
          ctx,
          scope,
          since: new Date(query.since),
          ...(query.until === undefined ? {} : { until: new Date(query.until) }),
          arrived: deps.now(),
        };
      },
      applyPage: landPage,
      newestCreatedAt: (page) => newestCreatedAtOf(page.items),
      now: deps.now,
      report: deps.report,
    });
  const workspaceBinding = bindWithLadder(FEED_ITEM_WORKSPACE_LADDER_HOURS);
  const batchBinding = bindWithLadder(FEED_BATCH_LADDER_HOURS);
  /** The batch scopes the last `syncFeeds` walked, by workspace, so `backfilled` can wait on each one's fill. */
  const batchScopes = new Map<string, FeedItemLogScope[]>();

  return {
    sync: (ctx) => workspaceBinding.sync(ctx, WORKSPACE_SCOPE),
    syncFeeds: async (ctx, feedIds, feedsPerBatch) => {
      const sorted = [...new Set(feedIds)].sort();
      const scopes: FeedItemLogScope[] = [];
      for (let start = 0; start < sorted.length; start += feedsPerBatch) {
        scopes.push(feedBatchScope(sorted.slice(start, start + feedsPerBatch)));
      }
      batchScopes.set(ctx.workspaceId, scopes);
      const keys = new Set(scopes.map((scope) => coverageKeyFor(feedItemsLog, scope)));
      const batchKeysAt = `${ctx.workspaceId}:${FEED_BATCH_KEY_PREFIX}`;
      for (const at of [...held.keys()]) {
        if (at.startsWith(batchKeysAt) && !keys.has(at.slice(ctx.workspaceId.length + 1))) held.delete(at);
      }
      await deps.coverage.prune?.(FEED_BATCH_KEY_PREFIX, ctx.workspaceId, keys);
      await mapWithConcurrency(scopes, FEED_BATCH_CONCURRENCY, (scope) => batchBinding.sync(ctx, scope));
    },
    backfilled: async (ctx) => {
      await Promise.all([
        workspaceBinding.backfilled(ctx, WORKSPACE_SCOPE),
        ...(batchScopes.get(ctx.workspaceId) ?? []).map((scope) => batchBinding.backfilled(ctx, scope)),
      ]);
    },
  };
}

/** What one landed page wrote: the feeds it touched and how many items. */
export type FeedItemPageStats = { feedIds: readonly string[]; itemCount: number };

/**
 * The held span each walk last carried to the head this session: under the workspace id for a workspace-wide walk,
 * under `${workspaceId}:${feedId}` for each feed a batch walk named.
 */
const sessionHeads = new Map<string, CoveredSpan>();

const pageListeners = new Set<(stats: FeedItemPageStats) => void>();

/**
 * Calls `listener` after every page of the item log lands, until the returned function is called.
 *
 * The warmup counts what its walk wrote through this.
 */
export function onFeedItemPageLanded(listener: (stats: FeedItemPageStats) => void): () => void {
  pageListeners.add(listener);
  return () => {
    pageListeners.delete(listener);
  };
}

/** The changed fields of `feed` after folding a page's items into its span. */
function spanUpdates(feed: Feed, createdAts: readonly string[], page: LandedFeedItemPage): Partial<Feed> {
  const { span, held } = foldFeedSpan(
    { oldest: feed.oldestContiguousCreatedAt, newest: feed.newestContiguousCreatedAt },
    createdAts,
    page.covered,
    page.reachesHead,
  );
  const updates: Partial<Feed> = {};
  if (span !== null && span.oldest !== feed.oldestContiguousCreatedAt) updates.oldestContiguousCreatedAt = span.oldest;
  if (span !== null && span.newest !== feed.newestContiguousCreatedAt) updates.newestContiguousCreatedAt = span.newest;
  if (held && !feed.loadedLastPage) updates.loadedLastPage = true;
  return updates;
}

/**
 * Keeps the head span current for what the page's read covered: a page that reached the head replaces it, and a page
 * whose held span contains the head's edge has grown it downward.
 */
function recordSessionHead(workspaceId: string, page: LandedFeedItemPage): void {
  if (page.covered === null) return;
  const covered = page.covered;
  const keys = page.feedIds === undefined ? [workspaceId] : page.feedIds.map((feedId) => `${workspaceId}:${feedId}`);
  for (const key of keys) {
    const head = sessionHeads.get(key);
    const grewHead = head !== undefined && time(covered.from) <= time(head.to) && time(covered.to) >= time(head.to);
    if (page.reachesHead || grewHead) sessionHeads.set(key, covered);
  }
}

/**
 * Lands one page: upserts its items, grows each touched feed's contiguity span, and queues an unread pass for them.
 *
 * Items enter the store read, so the touched feeds get their own unread pass, with `alsoSetAsRead` so a receipt that
 * already arrived clears what it reads.
 */
async function landFeedItemPage(ctx: SyncContext, page: LandedFeedItemPage): Promise<void> {
  recordSessionHead(ctx.workspaceId, page);
  if (page.items.length === 0) return;
  await bulkUpsertItemAndCalculateMembership(page.items);

  const createdAtsByFeed = new Map<string, string[]>();
  for (const item of page.items) {
    if (item.createdAt == null) continue;
    const createdAts = createdAtsByFeed.get(item.feedId) ?? [];
    createdAts.push(item.createdAt);
    createdAtsByFeed.set(item.feedId, createdAts);
  }
  store.batch(() => {
    for (const [feedId, createdAts] of createdAtsByFeed) {
      const feed = store.get("feed", feedId);
      if (feed === undefined) continue;
      const updates = spanUpdates(feed, createdAts, page);
      if (Object.keys(updates).length > 0) store.update("feed", feedId, (row) => ({ ...row, ...updates }));
    }
  });

  const feedIds = [...new Set(page.items.map((item) => item.feedId))];
  const membership = rowsByIndex("workspaceMembership", idx.membershipsByAccount, ctx.accountId).find(
    (row) => row.workspaceId === ctx.workspaceId && row.status === "active",
  );
  if (membership !== undefined) {
    enqueueCalculateUnreads({
      membership: { ...membership, accountId: membership.accountId ?? null },
      feedIds,
      alsoSetAsRead: true,
    });
    // The items entered the store read, and a page that lands inside the throttle's window would wait out the window
    // read: a member who opens one meanwhile sends no receipt for it. Run the pass now.
    flushCalculateUnreads();
  }
  for (const listener of pageListeners) listener({ feedIds, itemCount: page.items.length });
}

/**
 * The production sync for each lookback a workspace has asked for: the engine's depth is fixed when it is built.
 *
 * A lookback changed mid-session builds a second sync beside the first. Each keeps its own in-memory spans and lock,
 * and both write the same coverage key, so the last to flush can drop the other's progress. That is accepted: items
 * upsert by id, and the next boot walks whatever was lost.
 */
const productions = new Map<number, FeedItemSync>();

function productionFor(hours: number): FeedItemSync {
  const known = productions.get(hours);
  if (known !== undefined) return known;
  const built = createFeedItemSync({
    depth: { hours },
    fetchPage: (query) => listFeedItemEventLog(transport, query),
    coverage: registryCoverageStore,
    land: landFeedItemPage,
    report: (name, attributes) => recordEvent(name, attributes, "reliability"),
    now: serverNow,
  });
  productions.set(hours, built);
  return built;
}

/**
 * Brings the workspace's last `lookbackHours` of items up to date in one workspace-wide walk.
 *
 * On a cold boot it resolves once the last twelve hours have landed, with the rest of the lookback still loading in
 * the background; on a warm boot, or a ping, once the items created since the last walk have landed. The lookback is
 * the workspace's `web.warmup.lookbackHours` unless the caller hands over the settings it resolved.
 */
export function syncFeedItems(
  ctx: SyncContext,
  settings: Pick<WarmupSettings, "lookbackHours"> = warmupSettingsFor(ctx.workspaceId),
): Promise<void> {
  return productionFor(settings.lookbackHours).sync(ctx);
}

/**
 * Brings the last `lookbackHours` of `feedIds`' items up to date, `feedsPerBatch` feeds a read.
 *
 * Each batch is its own walk of the log through the `feedIds` lens, so its cost follows those feeds' items. Resolves
 * once every batch's lookback has landed. The settings are the workspace's `web.warmup` unless the caller hands
 * over the ones it resolved.
 */
export function syncFeedBatches(
  ctx: SyncContext,
  feedIds: readonly string[],
  settings: Pick<WarmupSettings, "lookbackHours" | "feedsPerBatch"> = warmupSettingsFor(ctx.workspaceId),
): Promise<void> {
  return productionFor(settings.lookbackHours).syncFeeds(ctx, feedIds, settings.feedsPerBatch);
}

/**
 * Resolves once the walks `syncFeedItems` or `syncFeedBatches` left loading in the background have landed, for the
 * same `lookbackHours`; at once when nothing is loading. Never rejects. The boot's warmup awaits it after its walk.
 */
export function feedItemsBackfilled(
  ctx: SyncContext,
  settings: Pick<WarmupSettings, "lookbackHours"> = warmupSettingsFor(ctx.workspaceId),
): Promise<void> {
  return productionFor(settings.lookbackHours).backfilled(ctx);
}

/**
 * Whether the item log holds every item of `feed` up to its head: the feed's span reaches into the held span that a
 * walk this session carried to the head, workspace-wide or in a batch that named the feed.
 *
 * Only a walk this session counts. A warm boot restores the store from a snapshot that predates everything sent while
 * the app was closed, and from then on the realtime subscription keeps the head current, as it does for
 * `loadedLastPage`. A feed held this way opens from the store, however few items it has.
 */
export function feedHeldThroughLogHead(
  workspaceId: string,
  feed: Pick<Feed, "id" | "oldestContiguousCreatedAt" | "newestContiguousCreatedAt"> | undefined,
): boolean {
  const oldest = feed?.oldestContiguousCreatedAt;
  const newest = feed?.newestContiguousCreatedAt;
  const head =
    feed === undefined ? undefined : (sessionHeads.get(workspaceId) ?? sessionHeads.get(`${workspaceId}:${feed.id}`));
  if (!oldest || !newest || head === undefined) return false;
  return time(newest) >= time(head.from) && time(oldest) <= time(head.to);
}
